import type { VisualSignalName } from '../signals/VisualSignal.js';

/**
 * A disturbance, before anything has decided what it looks like.
 *
 * Until now a wave *was* a ripple: the word and the renderer were the same
 * thing, so "put a wave here" could only ever mean "draw an expanding outline".
 * That is the assumption this file removes. A wave event says that something
 * happened, where it happened, how hard, and which way it was going — and says
 * nothing at all about ink, smoke, pigment or text. The material is chosen
 * afterwards, by `WaveDirector`, and the same event handed to two materials has
 * to come out as two genuinely different pictures.
 *
 * Everything here is a plain number or string. No renderer type appears in this
 * file except as a `string`, because the set of materials is whatever the host
 * registered factories for, and this layer must not know that list.
 *
 * ## Coordinates
 *
 * `x` and `y` are in **field units**: 1 is the radius of the playfield, so
 * (0, 0) is the centre and the unit circle is the field's edge. A renderer
 * multiplies by its own `fieldRadius` to get world units. Storing them this way
 * rather than in world units is what lets a spawn sequence be compared across
 * two runs, and across a live run and a 4K offline re-render, without the
 * numbers changing meaning.
 */

export interface WaveEvent {
  /** Monotonic within a run. Identifies this event in a spawn log. */
  readonly index: number;
  /** Song time, in seconds. */
  readonly time: number;
  /** Field units: 1 is the playfield radius, (0, 0) is the centre. */
  readonly x: number;
  readonly y: number;
  /** Radians. Which way the disturbance is travelling. */
  readonly direction: number;
  /** 0..1, roughly "how hard". Drives amplitude, count and reach. */
  readonly strength: number;
  /** Overall size multiplier, around 1. */
  readonly scale: number;
  /** Seconds. What the material should aim to still be showing. */
  readonly lifetime: number;
  /**
   * This event's own seed.
   *
   * Derived from the run's seed and the event's index, so a material can take
   * as many numbers as it likes without moving what the next event does.
   */
  readonly seed: number;
  /** The renderer id chosen for it, e.g. `SUMI`. */
  readonly material: string;
  /**
   * A word for the character of the event, which materials read as they like.
   *
   * `clean` and `struck` are ordinary; `torn` is what a miss produces; `stray`
   * is a press that judged nothing. A material is free to ignore this, but the
   * ones that honour it are how a run's history stays visible.
   */
  readonly style: WaveStyle;
  /** Which signal caused it, for diagnostics and for the spawn log. */
  readonly source: VisualSignalName | 'AMBIENT';
  /** Which placement rule produced the position. */
  readonly placement: string;
  /**
   * The other materials answering this same disturbance, if any.
   *
   * This is how two materials coordinate without knowing about each other. A
   * renderer is told *that* it is part of a group and *who else* is in it — by
   * name, as a string — and decides for itself what to do about that. Smoke
   * does not import code; code notices smoke is in its group and drifts.
   *
   * Absent when the wave went to a single material, which is every mode
   * except COLLISION.
   */
  readonly collision?: WaveCollision;
}

export interface WaveCollision {
  /** Shared by every event in the group, so a renderer can tell them apart. */
  readonly id: number;
  /** Every material answering this disturbance, including this one. */
  readonly materials: readonly string[];
  /** This material's position in the group. 0 leads. */
  readonly index: number;
}

export const WAVE_STYLES = ['clean', 'struck', 'torn', 'stray', 'swell'] as const;
export type WaveStyle = (typeof WAVE_STYLES)[number];

export function isWaveStyle(value: string): value is WaveStyle {
  return (WAVE_STYLES as readonly string[]).includes(value);
}

/**
 * One line of a spawn log.
 *
 * The determinism tests compare these rather than pixels. A frame hash proves
 * two runs *looked* the same; this proves they *decided* the same, which is the
 * thing that has to hold before the pixels can, and the thing that says which
 * part broke when they differ.
 */
export interface WaveRecord {
  readonly index: number;
  readonly material: string;
  readonly placement: string;
  readonly style: WaveStyle;
  readonly x: number;
  readonly y: number;
  readonly direction: number;
  readonly seed: number;
  /** The collision group, or an empty string. */
  readonly group: string;
}

/** Rounded to a fixed number of places, so two logs compare as strings. */
export function waveRecord(event: WaveEvent): WaveRecord {
  return {
    index: event.index,
    material: event.material,
    placement: event.placement,
    style: event.style,
    x: round(event.x),
    y: round(event.y),
    direction: round(event.direction),
    seed: event.seed,
    group: event.collision === undefined ? '' : event.collision.materials.join('+'),
  };
}

/** A whole log as one comparable string. */
export function waveLog(events: readonly WaveEvent[]): string {
  return events
    .map((event) => {
      const record = waveRecord(event);
      return `${record.index}|${record.material}|${record.placement}|${record.style}|` +
        `${record.x}|${record.y}|${record.direction}|${record.group}`;
    })
    .join('\n');
}

function round(value: number): number {
  return Math.round(value * 1e4) / 1e4;
}
